<template>
    <slot />
    <Teleport to="body">
        <pbl-dialog
            ref="dialogRef"
            :headline="!isNoActionSet ? t('common.conditionStatus.noAction.modal.titles.setCategory') : t('common.conditionStatus.noAction.modal.titles.removeCategory')"
            click-shield
            class="no-action-category-dialog"
        >
            <div class="no-action-category-wrapper">
                <span class="no-action-category-wrapper__description">
                    {{
                        !isNoActionSet
                            ? t('common.conditionStatus.noAction.modal.descriptions.setCategory', descriptionParams)
                            : t('common.conditionStatus.noAction.modal.descriptions.removeCategory', descriptionParams)
                    }}
                </span>
                <VeeTextareaField
                    name="comment"
                    :label="t('common.conditionStatus.noAction.modal.labels.comment')"
                    :placeholder="t('formInputs.common.placeholders.enterValue')"
                    :maxlength="COMMENT_MAX_LENGTH"
                />
            </div>

            <pbl-button
                slot="footer"
                variant="secondary"
                @click="closeModal"
            >
                {{ t('common.conditionStatus.noAction.modal.actions.cancel') }}
            </pbl-button>
            <pbl-button
                slot="footer"
                :disabled="!meta.dirty || !meta.valid || isAddPending || isDeletePending"
                @click="onSubmit"
            >
                {{ t('common.conditionStatus.noAction.modal.actions.confirm') }}
            </pbl-button>
        </pbl-dialog>
    </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import { PebbleDialog } from '@pebble/core'
import { NoActionCategoryPayload, provideNoActionCategoryModal } from '@/pages/transformers/condition-status/modals/no-action-category/useConditionStatusNoActionCategoryModal'
import VeeTextareaField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeTextareaField.vue'
import { useForm } from 'vee-validate'
import { noActionCategoryValidationSchema } from '@/pages/transformers/condition-status/modals/no-action-category/ConditionStatusNoActionCategoryModal.form'
import { AxiosError } from 'axios'
import { NoActionComponentDto } from '@/api/generated/HCA'
import { useNoActionAddMutation } from '@/api/mutations/general/useNoActionAddMutation'
import { useNoActionDeleteMutation } from '@/api/mutations/general/useNoActionDeleteMutation'
import { ComponentType, ComponentTypesMap } from '@/configuration/component/component-types'
import { NoActionPayload } from '@/types/no-action'

const props = defineProps<{
    componentType: ComponentType
    isNoActionSet: boolean
    componentSid: string
    noActionComponent?: NoActionComponentDto
}>()

const COMMENT_MAX_LENGTH = 250

const { t } = useI18n()
const toasts = usePebbleToasts()

const { mutateAsync: mutateAddAsync, isPending: isAddPending } = useNoActionAddMutation()
const { mutateAsync: mutateDeleteAsync, isPending: isDeletePending } = useNoActionDeleteMutation()

const dialogRef = ref<PebbleDialog | null>(null)

const descriptionParams = computed(() => ({
    componentType: t(ComponentTypesMap[props.componentType].labelKey),
    componentSid: props.componentSid,
}))

const { handleSubmit, resetForm, meta } = useForm({
    validationSchema: noActionCategoryValidationSchema,
    initialValues: {
        comment: '',
    },
})

const openModal = (payload?: NoActionCategoryPayload) => {
    resetForm({
        values: {
            comment: payload?.noActionCategory?.comment ?? '',
        },
    })

    dialogRef.value?.show()
}

const closeModal = () => {
    dialogRef.value?.hide()
}

const onSubmit = handleSubmit(async (values) => {
    const payload: NoActionPayload = {
        componentSid: props.componentSid,
        noActionData: {
            ...props.noActionComponent,
            comment: values.comment,
            componentSid: props.componentSid,
            componentType: props.componentType,
        },
    }

    try {
        !props.isNoActionSet ? await mutateAddAsync(payload) : await mutateDeleteAsync(payload)
        closeModal()
    } catch (err) {
        if (err instanceof AxiosError) {
            toasts?.error({
                message: t('common.errors.unknown'),
                duration: 3000,
            })
        }
    }
})

provideNoActionCategoryModal({
    openModal,
})
</script>

<style scoped lang="scss">
.no-action-category-dialog {
    --width: 70vw;
}

.no-action-category-wrapper {
    display: flex;
    flex-direction: column;
    gap: $spacing-l;

    &__description {
        color: $pbl-text-muted;
    }
}
</style>
